import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLayout } from "@/contexts/UseLayout";
import ChatService from "@/services/ChatService";
import CadastroDeskService from "@/services/cadastroDesk";
import { getSocket } from "@/services/socketService";
import { toast } from "react-toastify";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence } from "framer-motion";
import {
  X,
  Users,
  ArrowDown as IconArrowDown,
  File,
  Download,
  Settings,
  Check,
  CheckCheck,
  Eye,
  EyeOff,
  ChevronUp,
  SquareArrowOutUpRight,
  Folder,
  MessageCircle,
  Phone,
} from "lucide-react";
import ImageGeneric from "@/assets/images/lg.png";
import { EditGroupModal } from "./EditGroupModal";
import { AudioRecorder } from "../components/AudioRecorder";
import { AudioPlayer } from "../components/AudioPlayer";
import { MixedMessage } from "../components/MixedMessage";
import {
  IconAudioChat,
  IconSendChat,
  IconSmileChat,
  IconUploadChat,
} from "@/assets/images/Icons";
import {
  MessageActions,
  ReplyPreview,
  ReplyQuote,
  DeleteConfirmModal,
  DeletedMessagePlaceholder,
  FileUploadModal,
  FilesAndLinksModal,
  CallHistoryMessage,
} from "@/components/Chat/shared";
import type {
  ChatMessage,
  FileUploadItem,
  MessageDeletedPayload,
} from "@/types/chat";
import { MAX_FILE_SIZE } from "@/types/chat";
import {
  confirmSentMessage,
  mergeChatMessages,
  upsertRealtimeMessage,
  withoutTempMessage,
} from "@/utils/chatRealtime";
import { ResumoDesk } from "@/pages/UserGoOn/Desks/ResumoDesk";

const socket = getSocket();

const quickEmojis = [
  "😀",
  "😂",
  "😍",
  "🤔",
  "😭",
  "😘",
  "😉",
  "😁",
  "😒",
  "😜",
  "🤢",
  "😎",
  "🙌",
  "👀",
  "👍",
  "👎",
  "❤️",
  "🔥",
  "✨",
  "🎉",
  "🤦‍♀️",
  "🤦‍♂️",
  "🤷‍♀️",
  "🤷‍♂️",
];

type MentionUser = {
  id: number;
  name?: string;
  last_name?: string;
  email?: string;
  url_img?: { path?: string } | string | null;
};

type ActiveMention = {
  start: number;
  query: string;
};

type RealtimeMessagePayload = Record<string, unknown>;

const normalizeMentionText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const getMentionUserName = (user?: MentionUser | null) =>
  `${user?.name ?? ""} ${user?.last_name ?? ""}`.trim();

const getMentionUserAvatar = (user?: MentionUser | null) => {
  const image = user?.url_img;
  if (typeof image === "string") return image;
  return image?.path;
};

const normalizeRealtimeMessage = (data: RealtimeMessagePayload) => ({
  id: data.id as ChatMessage["id"],
  content: String(data.content ?? ""),
  sender_id: (data.senderId ||
    data.sender_id ||
    data.usuario_send) as ChatMessage["sender_id"],
  created_at: String(
    data.createdAt || data.created_at || new Date().toISOString(),
  ),
  updated_at: data.updatedAt || data.updated_at,
  chat_room_id: (data.chatRoomId ||
    data.chat_room_id) as ChatMessage["chat_room_id"],
  files: (data.files || "[]") as ChatMessage["files"],
  file_names: (data.file_names || []) as ChatMessage["file_names"],
  messageType: (data.messageType ||
    data.message_type ||
    "text") as ChatMessage["messageType"],
  reply_to: (data.reply_to || null) as ChatMessage["reply_to"],
  reply_to_id: (data.reply_to_id ||
    data.replyToId ||
    null) as ChatMessage["reply_to_id"],
  readBy: (data.readBy || []) as ChatMessage["readBy"],
  totalReads: Number(data.totalReads || 0),
  deleted: Boolean(data.deleted),
  event_type: (data.event_type || "message") as ChatMessage["event_type"],
  metadata: (data.metadata || null) as ChatMessage["metadata"],
  chat_call_id: (data.chat_call_id || null) as ChatMessage["chat_call_id"],
});

const getActiveMention = (
  value: string,
  caretPosition: number,
): ActiveMention | null => {
  const beforeCaret = value.slice(0, caretPosition);
  const triggerIndex = beforeCaret.lastIndexOf("@");

  if (triggerIndex === -1) return null;

  const charBeforeTrigger = beforeCaret[triggerIndex - 1];
  if (charBeforeTrigger && !/\s/.test(charBeforeTrigger)) return null;

  const query = beforeCaret.slice(triggerIndex + 1);
  if (query.includes("\n") || query.length > 60) return null;
  if (/[,.;:!?()[\]{}]/.test(query)) return null;

  return {
    start: triggerIndex,
    query,
  };
};

interface ChatWindowProps {
  user: any;
  room: any;
  onClose: (options?: { preserveCall?: boolean }) => void;
  onMinimize: () => void;
  isMinimized: boolean;
  setMenuOpen?: (value: boolean) => void;
  setSelectedOption?: (value: any) => void;
}

export function ChatWindowImproved({
  room,
  onClose,
  setMenuOpen,
  onMinimize,
  isMinimized,
  setSelectedOption,
}: ChatWindowProps) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasInitialScrolled, setHasInitialScrolled] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [showReadInfo, setShowReadInfo] = useState<string | null>(null);
  const [hoveredMessage, setHoveredMessage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"chat" | "readInfo">("chat");
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState<number>(0);
  const [showAudioRecorder, setShowAudioRecorder] = useState<boolean>(false);
  const [openingDeskComments, setOpeningDeskComments] = useState(false);
  const [deskCommentsData, setDeskCommentsData] = useState<any>(null);
  const navigate = useNavigate();

  // Novos estados para Reply, Delete, Files
  const [replyToMessage, setReplyToMessage] = useState<ChatMessage | null>(
    null,
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<ChatMessage | null>(
    null,
  );
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(
    null,
  );
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionProfile, setMentionProfile] = useState<{
    user: MentionUser;
    top: number;
    left: number;
  } | null>(null);

  // Auto-resize do textarea
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    setActiveMention(getActiveMention(value, e.target.selectionStart));
    setSelectedMentionIndex(0);

    // Reset height para calcular o novo tamanho
    e.target.style.height = "auto";

    // Define a nova altura baseada no scrollHeight
    const newHeight = Math.min(e.target.scrollHeight, 120); // Máximo de ~5 linhas
    e.target.style.height = `${newHeight}px`;
  };

  // Reset textarea height quando mensagem é enviada
  useEffect(() => {
    if (newMessage === "" && textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [newMessage]);

  // Função para verificar se um arquivo é áudio pela extensão
  const isAudioFile = (filename: string) => {
    const audioExtensions = [".webm", ".mp3", ".wav", ".ogg", ".m4a", ".aac"];
    const extension = filename
      .toLowerCase()
      .substring(filename.lastIndexOf("."));
    return audioExtensions.includes(extension);
  };

  // Função para extrair nome do arquivo da URL
  const getFilenameFromUrl = (url: string) => {
    try {
      const urlParts = url.split("/");
      return urlParts[urlParts.length - 1];
    } catch {
      return "";
    }
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const mentionListRef = useRef<HTMLDivElement>(null);
  const mentionProfileRef = useRef<HTMLDivElement>(null);
  const readInfoContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const pendingMessageSeqRef = useRef(0);
  // Controla se já foi feito o carregamento inicial (para não resetar scroll em reloads subsequentes)
  const hasLoadedInitially = useRef(false);

  const { userLogged, getChats, startChatVoiceCall, activeChatCall, isChatCallBusy } =
    useLayout();
  const isGroupChat =
    room?.is_group === true ||
    room?.is_group === 1 ||
    room?.is_group === "1" ||
    room?.is_group === "true";

  const createTempMessageId = () => {
    pendingMessageSeqRef.current += 1;
    return `temp-${Date.now()}-${pendingMessageSeqRef.current}`;
  };

  const roomParticipants = useMemo(() => {
    const participants = new Map<number, MentionUser>();

    if (Array.isArray(room?.users)) {
      room.users.forEach((user: MentionUser) => {
        const userId = Number(user?.id);
        if (Number.isFinite(userId) && getMentionUserName(user)) {
          participants.set(userId, { ...user, id: userId });
        }
      });
    }

    const loggedUserId = Number(userLogged?.data?.id);
    if (
      Number.isFinite(loggedUserId) &&
      getMentionUserName(userLogged?.data) &&
      !participants.has(loggedUserId)
    ) {
      participants.set(loggedUserId, {
        id: loggedUserId,
        name: userLogged?.data?.name,
        last_name: userLogged?.data?.last_name,
        email: userLogged?.data?.email,
        url_img: userLogged?.data?.url_img,
      });
    }

    return Array.from(participants.values());
  }, [room?.users, userLogged?.data]);

  const mentionableUsers = useMemo(() => {
    const loggedUserId = Number(userLogged?.data?.id);
    return roomParticipants.filter((user) => user.id !== loggedUserId);
  }, [roomParticipants, userLogged?.data?.id]);

  const mentionPatterns = useMemo(
    () =>
      roomParticipants
        .map((user) => ({
          user,
          mention: `@${getMentionUserName(user)}`,
        }))
        .filter((item) => item.mention.length > 1)
        .sort((a, b) => b.mention.length - a.mention.length),
    [roomParticipants],
  );

  const filteredMentionUsers = useMemo(() => {
    if (!activeMention) return [];

    const query = normalizeMentionText(activeMention.query);
    return mentionableUsers
      .filter((user) => {
        const name = normalizeMentionText(getMentionUserName(user));
        const email = normalizeMentionText(user.email ?? "");
        return !query || name.includes(query) || email.includes(query);
      })
      .slice(0, 6);
  }, [activeMention, mentionableUsers]);

  useEffect(() => {
    if (selectedMentionIndex >= filteredMentionUsers.length) {
      setSelectedMentionIndex(0);
    }
  }, [filteredMentionUsers.length, selectedMentionIndex]);

  const getDeskIdFromRoom = () => {
    const candidates = [
      room?.addesk_id,
      room?.id_addesk,
      room?.desk_id,
      room?.id_desk,
      room?.addesk?.id,
      room?.desk?.id,
      room?.metadata?.addesk_id,
      room?.metadata?.desk_id,
    ];

    return candidates.find(
      (value) => value !== null && value !== undefined && value !== "",
    );
  };

  const deskId = getDeskIdFromRoom();

  const handleOpenDeskComments = async () => {
    if (!deskId || openingDeskComments) return;

    setOpeningDeskComments(true);
    try {
      const res = await CadastroDeskService.getDesk(deskId);
      if (res?.status === 200) {
        setDeskCommentsData(res.data);
      } else {
        toast.error("Erro ao abrir comentários da Desk");
      }
    } catch (error) {
      toast.error("Erro ao abrir comentários da Desk");
    } finally {
      setOpeningDeskComments(false);
    }
  };

  // Verificar se o usuário é admin do grupo
  const isGroupAdmin =
    isGroupChat && room?.admin_ids?.includes(userLogged?.data?.id);

  // Função para recarregar dados do chat após edição
  const handleGroupUpdate = () => {
    // Aqui você pode recarregar os dados do chat se necessário
    getChats(); // Atualiza a lista de chats
  };

  // Função para determinar o status de leitura da mensagem
  const getMessageReadStatus = (message: any) => {
    if (message.sender_id === userLogged?.data?.id) {
      // Mensagem própria - verificar quantos leram
      const totalReads = message.totalReads || 0;
      if (totalReads > 0) {
        return { status: "read", count: totalReads };
      } else {
        return { status: "sent", count: 0 };
      }
    }
    return { status: "unread", count: 0 };
  };

  // Função para abrir informações de leitura
  const handleOpenReadInfo = (message: any) => {
    // Salvar a posição atual do scroll do chat
    const container = messagesContainerRef.current;
    if (container) {
      setSavedScrollPosition(container.scrollTop);
    }

    setSelectedMessage(message);
    setViewMode("readInfo");
  };

  // Função para voltar ao chat
  const handleBackToChat = () => {
    setViewMode("chat");
    setSelectedMessage(null);
    setShowReadInfo(null);

    // Restaurar a posição do scroll do chat após a mudança de viewMode
    setTimeout(() => {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = savedScrollPosition;
      }
    }, 0);
  };

  // Renderizar página de informações de leitura
  const renderReadInfoPage = () => {
    if (!selectedMessage?.readBy) return null;

    const readUsers = selectedMessage.readBy.filter((user: any) => user.isRead);
    const unreadUsers = selectedMessage.readBy.filter(
      (user: any) => !user.isRead,
    );

    return (
      <div
        ref={readInfoContainerRef}
        className="flex-1 overflow-y-auto bg-gray-50"
      >
        <div className="bg-white border p-1 pb-2 space-y-1">
          <span className="text-sm text-gray-500 font-medium">
            {t("chatModule.read_info.message")}:
          </span>
          <div className="bg-gray-50 shadow rounded-lg p-3 border-l-4 border-azul-400">
            <p className="text-xs text-gray-700 line-clamp-2">
              {selectedMessage.content}
            </p>
          </div>
        </div>

        {readUsers.length > 0 && (
          <div className="bg-white p-1 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Eye size={16} className="text-green-600" />
              <h4 className="font-medium text-gray-900 text-sm">
                {t("chatModule.read_info.viewed_by", {
                  count: readUsers.length,
                })}
              </h4>
            </div>
            <div className="space-y-3">
              {readUsers.map((user: any, index: number) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-medium text-sm">
                        {user.userName?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm truncate max-w-44">
                        {user.userName} {user.userLastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate max-w-44">
                        {user.readAt
                          ? new Date(user.readAt).toLocaleString("pt-BR")
                          : t("chatModule.read_info.now")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Check size={14} className="text-green-600 ml-auto" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {unreadUsers.length > 0 && (
          <div className="bg-white p-1 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <EyeOff size={16} className="text-gray-400" />
              <h4 className="font-medium text-gray-900 text-sm">
                {t("chatModule.read_info.not_viewed", {
                  count: unreadUsers.length,
                })}
              </h4>
            </div>
            <div className="space-y-2">
              {unreadUsers.map((user: any, index: number) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-gray-500 font-medium text-sm">
                        {user.userName?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm truncate max-w-44">
                        {user.userName} {user.userLastName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("chatModule.read_info.not_viewed_single")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <EyeOff size={14} className="text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Função para adicionar emoji
  const addEmoji = (emoji: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue =
        newMessage.substring(0, start) + emoji + newMessage.substring(end);
      setNewMessage(newValue);

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    } else {
      setNewMessage((prev) => prev + emoji);
    }
    setShowEmojiPicker(false);
  };

  const insertMention = (user: MentionUser) => {
    const textarea = textareaRef.current;
    const caretPosition = textarea?.selectionStart ?? newMessage.length;
    const mentionRange =
      getActiveMention(newMessage, caretPosition) ?? activeMention;

    if (!mentionRange) return;

    const mentionText = `@${getMentionUserName(user)} `;
    const nextMessage =
      newMessage.slice(0, mentionRange.start) +
      mentionText +
      newMessage.slice(caretPosition);
    const nextCaretPosition = mentionRange.start + mentionText.length;

    setNewMessage(nextMessage);
    setActiveMention(null);
    setSelectedMentionIndex(0);

    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(
        nextCaretPosition,
        nextCaretPosition,
      );
    }, 0);
  };

  const handleMentionProfileOpen = (
    user: MentionUser,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const width = 256;
    const height = 180;

    setMentionProfile({
      user,
      top: Math.max(
        8,
        Math.min(rect.bottom + 8, window.innerHeight - height - 8),
      ),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
    });
  };

  const messageMentionsCurrentUser = (content?: string) => {
    const currentUserId = Number(userLogged?.data?.id);
    if (!Number.isFinite(currentUserId) || !content) return false;

    const currentUserMention = mentionPatterns.find(
      ({ user }) => user.id === currentUserId,
    );

    if (!currentUserMention) return false;

    const lowerContent = content.toLowerCase();
    const lowerMention = currentUserMention.mention.toLowerCase();
    let mentionIndex = lowerContent.indexOf(lowerMention);

    while (mentionIndex !== -1) {
      const nextCharacter = content[mentionIndex + lowerMention.length];
      if (!nextCharacter || /[\s.,;:!?()[\]{}]/.test(nextCharacter)) {
        return true;
      }
      mentionIndex = lowerContent.indexOf(lowerMention, mentionIndex + 1);
    }

    return false;
  };

  // Função para obter lista de membros do grupo
  const getGroupMembers = () => {
    if (!isGroupChat || !room?.users) return { names: "", fullList: "" };

    // Filtrar apenas outros usuários (excluir o próprio usuário logado)
    const otherUsers = room.users.filter(
      (u: any) => u.id !== userLogged?.data?.id,
    );

    // Criar lista de nomes
    const memberNames = otherUsers.map((u: any) => `${u.name}`);

    return {
      names: memberNames.join(", "),
      fullList: memberNames.join(", "),
    };
  };

  // Determinar o título do chat
  const getChatTitle = () => {
    if (isGroupChat) {
      return room.name;
    } else {
      const otherUser = room?.users?.find(
        (u: any) => u.id !== userLogged?.data?.id,
      );
      return otherUser
        ? `${otherUser.name} ${otherUser.last_name}`
        : t("chatModule.fallbacks.conversation");
    }
  };

  // Determinar a imagem do chat
  const getChatImage = () => {
    if (isGroupChat && room.group_image) {
      return room.group_image;
    } else {
      const otherUser = room?.users?.find(
        (u: any) => u.id !== userLogged?.data?.id,
      );
      return otherUser?.url_img?.path ?? ImageGeneric;
    }
  };

  // Carregar mensagens
  const loadMessages = useCallback(
    async (page = 1, reset = false) => {
      if (!room?.id) return;

      try {
        setIsLoadingMore(page > 1);
        isLoadingRef.current = page > 1; // Marcar se está carregando mensagens antigas

        const response = await ChatService.listarMensagens(room.id, page);

        if (response?.data?.messages?.data) {
          const newMessages = response.data.messages.data;

          if (reset) {
            if (!hasLoadedInitially.current) {
              hasLoadedInitially.current = true;
              setMessages((prev) => mergeChatMessages(prev, newMessages));
              setHasInitialScrolled(false);
            } else {
              setMessages((prev) => {
                const serverIds = new Set(
                  newMessages.map((message: any) => String(message.id)),
                );
                const optimisticOrRealtimeMessages = prev.filter(
                  (message) =>
                    String(message.id).startsWith("temp-") ||
                    !serverIds.has(String(message.id)),
                );

                return mergeChatMessages(
                  newMessages,
                  optimisticOrRealtimeMessages,
                );
              });
            }
          } else {
            // Salvar posição do scroll antes de adicionar mensagens antigas
            const container = messagesContainerRef.current;
            const scrollHeight = container?.scrollHeight || 0;

            setMessages((prev) => mergeChatMessages(newMessages, prev));

            // Ajustar scroll para manter posição após adicionar mensagens antigas
            setTimeout(() => {
              if (container && page > 1) {
                const newScrollHeight = container.scrollHeight;
                container.scrollTop = newScrollHeight - scrollHeight;
              }
            }, 100);
          }

          const totalPages = response?.data?.messages?.meta?.last_page || 1;
          setHasMoreMessages(page < totalPages);
          setCurrentPage(page);
        }
      } catch (error) {
        console.error("Erro ao carregar mensagens:", error);
      } finally {
        setIsLoadingMore(false);
        isLoadingRef.current = false;
      }
    },
    [room?.id],
  );

  // Carregar mais mensagens antigas
  const loadMoreMessages = useCallback(() => {
    if (!isLoadingMore && hasMoreMessages) {
      loadMessages(currentPage + 1, false);
    }
  }, [isLoadingMore, hasMoreMessages, currentPage, loadMessages]);

  // Handle scroll para carregar mais mensagens
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || !hasInitialScrolled || isLoadingRef.current) return;

    // Só carregar mais mensagens se scrollar para o topo (com margem de segurança)
    if (container.scrollTop <= 50 && !isLoadingMore && hasMoreMessages) {
      loadMoreMessages();
    }
  }, [loadMoreMessages, isLoadingMore, hasMoreMessages, hasInitialScrolled]);

  // Enviar mensagem
  const handleSendMessage = async () => {
    if (!newMessage.trim() && selectedFiles.length === 0) {
      return;
    }

    const messageToSend = newMessage;
    const filesToSend = [...selectedFiles];
    const tempId = createTempMessageId();

    // Limpar campos
    setNewMessage("");
    setSelectedFiles([]);

    // Adicionar mensagem otimista imediatamente para resposta instantânea
    const optimisticMessage = {
      id: tempId,
      content: messageToSend,
      sender_id: userLogged?.data?.id,
      created_at: new Date().toISOString(),
      files:
        filesToSend.length > 0
          ? JSON.stringify(filesToSend.map((f) => ({ name: f.name })))
          : "[]",
      chat_room_id: room.id,
      reply_to_id: replyToMessage?.id,
      messageType: filesToSend.length > 0 ? "file" : "text",
    };
    setMessages((prev) => mergeChatMessages(prev, [optimisticMessage]));

    setIsSending(true);
    try {
      const formData: any = new FormData();
      formData.append("message", messageToSend);
      formData.append("roomId", room.id);

      filesToSend.forEach((file) => {
        formData.append("files", file);
      });

      // Adicionar replyToId se estiver respondendo
      if (replyToMessage) {
        formData.append("replyToId", String(replyToMessage.id));
      }

      const response = await ChatService.enviarMensagem(formData);

      if (response?.data?.message) {
        setMessages((prev) =>
          confirmSentMessage(
            prev,
            tempId,
            normalizeRealtimeMessage(response.data.message),
          ),
        );
        getChats();
        setReplyToMessage(null);
      } else {
        // Remover placeholder temporário em caso de falha
        setMessages((prev) => withoutTempMessage(prev, tempId));
        toast.error(t("chatModule.errors.send_message"));
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      // Remover placeholder temporário em caso de erro
      setMessages((prev) => withoutTempMessage(prev, tempId));
      toast.error(t("chatModule.errors.send_message"));
    } finally {
      setIsSending(false);
    }
  };

  // Selecionar arquivos - agora abre o modal de upload
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setPendingFiles(files);
      setShowUploadModal(true);
    }
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Remover arquivo
  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Funções de áudio
  const handleAudioReady = async (audioBlob: Blob) => {
    const messageToSend = newMessage.trim();
    const tempId = createTempMessageId();

    const optimisticMessage = {
      id: tempId,
      content: messageToSend,
      sender_id: userLogged?.data?.id,
      created_at: new Date().toISOString(),
      files: JSON.stringify([{ name: "audio.webm", type: "audio/webm" }]),
      chat_room_id: room.id,
      reply_to_id: replyToMessage?.id,
      messageType: "audio",
    };

    setMessages((prev) => mergeChatMessages(prev, [optimisticMessage]));
    setIsSending(true);
    try {
      const formData = new FormData();
      formData.append("roomId", room.id);
      formData.append("files", audioBlob, "audio.webm");

      // Se houver mensagem de texto também
      if (messageToSend) {
        formData.append("message", messageToSend);
      }

      const response = await ChatService.enviarMensagem(formData);

      if (response?.data?.message) {
        setMessages((prev) =>
          confirmSentMessage(
            prev,
            tempId,
            normalizeRealtimeMessage(response.data.message),
          ),
        );
        getChats();
        setNewMessage("");
        setShowAudioRecorder(false);
        setReplyToMessage(null);
      } else {
        setMessages((prev) => withoutTempMessage(prev, tempId));
        toast.error(t("chatModule.errors.send_audio"));
      }
    } catch (error) {
      console.error("Erro ao enviar áudio:", error);
      setMessages((prev) => withoutTempMessage(prev, tempId));
      toast.error(t("chatModule.errors.send_audio"));
    } finally {
      setIsSending(false);
    }
  };

  const handleCancelAudio = () => {
    setShowAudioRecorder(false);
  };

  // ============================================
  // NOVOS HANDLERS: Reply, Delete, Upload, Paste
  // ============================================

  // Handler para iniciar reply
  const handleReply = (message: ChatMessage) => {
    // Não permitir reply em mensagem deletada
    if (message.deleted) {
      toast.warning(t("chatFeatures.reply.errorDeleted"));
      return;
    }
    // Não permitir reply de reply (limite de 1 nível)
    if (message.reply_to_id) {
      toast.warning(t("chatFeatures.reply.errorChain"));
      return;
    }
    setReplyToMessage(message);
    textareaRef.current?.focus();
  };

  // Handler para cancelar reply
  const handleCancelReply = () => {
    setReplyToMessage(null);
  };

  // Handler para iniciar delete
  const handleDeleteClick = (message: ChatMessage) => {
    setMessageToDelete(message);
    setShowDeleteModal(true);
  };

  // Handler para confirmar delete
  const handleConfirmDelete = async () => {
    if (!messageToDelete) return;

    try {
      const response = await ChatService.deletarMensagem(messageToDelete.id);

      if (response?.data?.success) {
        // Atualizar lista de mensagens localmente
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageToDelete.id
              ? { ...msg, deleted: true, deleted_at: new Date().toISOString() }
              : msg,
          ),
        );
        toast.success(t("chatFeatures.delete.success"));
      } else {
        toast.error(response?.data?.message || t("chatFeatures.delete.error"));
      }
    } catch (error) {
      console.error("Erro ao deletar mensagem:", error);
      toast.error(t("chatFeatures.delete.error"));
    } finally {
      setShowDeleteModal(false);
      setMessageToDelete(null);
    }
  };

  // Handler para copiar mensagem
  const handleCopyMessage = (message: ChatMessage) => {
    if (message.content) {
      navigator.clipboard.writeText(message.content);
      toast.success("Copiado!");
    }
  };

  // Handler para scroll até mensagem original (reply quote click)
  const handleScrollToMessage = (messageId: number) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const messageElement = container.querySelector(
      `[data-message-id="${messageId}"]`,
    );
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });

      // Adicionar highlight temporário
      messageElement.classList.add("ring-2", "ring-blue-400", "ring-offset-2");
      setTimeout(() => {
        messageElement.classList.remove(
          "ring-2",
          "ring-blue-400",
          "ring-offset-2",
        );
      }, 2000);
    }
  };

  // Handler para paste de arquivos (clipboard)
  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        // Verificar se é um arquivo (imagem ou outro)
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            // Verificar tamanho
            if (file.size > MAX_FILE_SIZE) {
              toast.error(t("chatFeatures.upload.fileTooLarge"));
              continue;
            }
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        setPendingFiles(files);
        setShowUploadModal(true);
      }
    },
    [t],
  );

  // Handler para envio via FileUploadModal
  const handleUploadModalSend = async (
    uploadItems: FileUploadItem[],
    uploadMessage: string,
  ) => {
    const tempId = createTempMessageId();
    const messageToSend = uploadMessage.trim();
    const optimisticMessage = {
      id: tempId,
      content: messageToSend,
      sender_id: userLogged?.data?.id,
      created_at: new Date().toISOString(),
      files: JSON.stringify(
        uploadItems.map((item) => ({
          name: item.file.name,
          displayName: item.displayName,
          type: item.type,
        })),
      ),
      chat_room_id: room.id,
      reply_to_id: replyToMessage?.id,
      messageType: uploadItems.length > 0 ? "file" : "text",
    };

    setMessages((prev) => mergeChatMessages(prev, [optimisticMessage]));

    const formData = new FormData();
    formData.append("roomId", room.id);

    if (messageToSend) {
      formData.append("message", messageToSend);
    }

    // Adicionar arquivos
    uploadItems.forEach((item) => {
      formData.append("files", item.file);
    });

    // Adicionar nomes de exibição customizados
    const displayNames = uploadItems.map((item) => {
      // Adicionar extensão de volta ao displayName se não tiver
      const extension = item.file.name.split(".").pop();
      const hasExtension = item.displayName
        .toLowerCase()
        .endsWith(`.${extension?.toLowerCase()}`);
      return hasExtension
        ? item.displayName
        : `${item.displayName}.${extension}`;
    });
    formData.append("fileDisplayNames", JSON.stringify(displayNames));

    // Adicionar replyToId se estiver respondendo
    if (replyToMessage) {
      formData.append("replyToId", String(replyToMessage.id));
    }

    try {
      const response = await ChatService.enviarMensagem(formData);

      if (response?.data?.message) {
        setMessages((prev) =>
          confirmSentMessage(
            prev,
            tempId,
            normalizeRealtimeMessage(response.data.message),
          ),
        );
        getChats();
        setReplyToMessage(null);
      } else {
        setMessages((prev) => withoutTempMessage(prev, tempId));
        toast.error(t("chatModule.errors.send_message"));
      }
    } catch (error) {
      console.error("Erro ao enviar arquivos:", error);
      setMessages((prev) => withoutTempMessage(prev, tempId));
      toast.error(t("chatModule.errors.send_message"));
      throw error;
    }
  };

  // Effect para adicionar listener de paste
  useEffect(() => {
    const container = messagesContainerRef.current?.parentElement;
    if (container) {
      container.addEventListener("paste", handlePaste as EventListener);
      return () => {
        container.removeEventListener("paste", handlePaste as EventListener);
      };
    }
  }, [handlePaste]);

  // Renderizar conteúdo da mensagem baseado no tipo
  const renderMessageContent = (message: any, isOwnMessage: boolean = true) => {
    const messageType = message.messageType || "text";
    const files = message.files || [];

    // Converter files de string para array se necessário
    let parsedFiles = files;
    if (typeof files === "string" && files !== "[]") {
      try {
        parsedFiles = JSON.parse(files);
      } catch (e) {
        parsedFiles = [];
      }
    }

    // Se não há messageType definido mas há arquivos, detectar automaticamente
    if (
      messageType === "text" &&
      Array.isArray(parsedFiles) &&
      parsedFiles.length > 0
    ) {
      const audioFiles = parsedFiles.filter((file: any) => {
        const filename = file.name || getFilenameFromUrl(file.url || file);
        return isAudioFile(filename);
      });

      const regularFiles = parsedFiles.filter((file: any) => {
        const filename = file.name || getFilenameFromUrl(file.url || file);
        return !isAudioFile(filename);
      });

      // Se todos os arquivos são áudios
      if (
        audioFiles.length > 0 &&
        regularFiles.length === 0 &&
        !message.content?.trim()
      ) {
        return <AudioPlayer files={audioFiles} isOwnMessage={isOwnMessage} />;
      }

      // Se há mix de conteúdo
      if (
        audioFiles.length > 0 ||
        regularFiles.length > 0 ||
        message.content?.trim()
      ) {
        return (
          <MixedMessage
            content={message.content}
            files={parsedFiles}
            isOwnMessage={isOwnMessage}
          />
        );
      }
    }

    switch (messageType) {
      case "audio":
        return <AudioPlayer files={parsedFiles} isOwnMessage={isOwnMessage} />;
      case "file":
        return (
          <div className="space-y-2">
            {parsedFiles.map((file: any, idx: number) => (
              <div
                key={idx}
                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-azul-300 hover:shadow-sm transition-all cursor-pointer group"
                onClick={() => window.open(file.url || file, "_blank")}
              >
                <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-azul-50 transition-colors">
                  <File size={20} className="text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {file.name || getFilenameFromUrl(file.url || file)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("chatModule.file")}
                  </p>
                </div>
                <div className="p-2 text-gray-400 group-hover:text-azul-500 transition-colors">
                  <Download size={18} />
                </div>
              </div>
            ))}
          </div>
        );
      case "mixed":
        return (
          <MixedMessage
            content={message.content}
            files={parsedFiles}
            isOwnMessage={isOwnMessage}
          />
        );
      default:
        return renderTextContent(message.content, isOwnMessage);
    }
  };

  const getMentionMatchAt = (content: string, index: number) => {
    if (content[index] !== "@") return null;

    return (
      mentionPatterns.find(({ mention }) => {
        const candidate = content.slice(index, index + mention.length);
        if (candidate.toLowerCase() !== mention.toLowerCase()) return false;

        const nextCharacter = content[index + mention.length];
        return !nextCharacter || /[\s.,;:!?()[\]{}]/.test(nextCharacter);
      }) ?? null
    );
  };

  const renderTextWithMentions = (
    content: string,
    keyPrefix: string,
    isOwnMessage: boolean,
  ) => {
    const nodes = [];
    let cursor = 0;

    while (cursor < content.length) {
      const mentionMatch = getMentionMatchAt(content, cursor);

      if (mentionMatch) {
        const isCurrentUserMention =
          mentionMatch.user.id === Number(userLogged?.data?.id);
        nodes.push(
          <button
            key={`${keyPrefix}-mention-${cursor}`}
            type="button"
            onClick={(event) =>
              handleMentionProfileOpen(mentionMatch.user, event)
            }
            className={`inline-flex max-w-full align-baseline font-semibold underline-offset-2 hover:underline ${
              isCurrentUserMention
                ? isOwnMessage
                  ? "text-white"
                  : "text-azul-400"
                : isOwnMessage
                  ? "text-white"
                  : "text-azul-400"
            }`}
            title="Ver perfil"
          >
            {content.slice(cursor, cursor + mentionMatch.mention.length)}
          </button>,
        );
        cursor += mentionMatch.mention.length;
        continue;
      }

      const nextMentionIndex = content.indexOf("@", cursor + 1);
      const endIndex =
        nextMentionIndex === -1 ? content.length : nextMentionIndex;
      nodes.push(
        <span
          key={`${keyPrefix}-text-${cursor}`}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {content.slice(cursor, endIndex)}
        </span>,
      );
      cursor = endIndex;
    }

    return nodes;
  };

  // Função para renderizar conteúdo de texto com links clicáveis
  const renderTextContent = (content: string, isOwnMessage: boolean = true) => {
    if (!content) return null;

    // Regex para detectar URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Dividir o texto em partes (URLs e texto normal)
    const parts = content.split(urlRegex);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline break-all"
            onClick={(e) => e.stopPropagation()}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {part}
          </a>
        );
      }
      return (
        <span key={index} style={{ whiteSpace: "pre-wrap" }}>
          {renderTextWithMentions(part, `part-${index}`, isOwnMessage)}
        </span>
      );
    });
  };

  // Formatação de data
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t("chatModule.dates.today");
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t("chatModule.dates.yesterday");
    } else {
      return date.toLocaleDateString("pt-BR");
    }
  };

  // Efeitos
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false);
      }

      if (
        mentionListRef.current &&
        !mentionListRef.current.contains(event.target as Node) &&
        !textareaRef.current?.contains(event.target as Node)
      ) {
        setActiveMention(null);
      }

      if (
        mentionProfile &&
        mentionProfileRef.current &&
        !mentionProfileRef.current.contains(event.target as Node)
      ) {
        setMentionProfile(null);
      }

      // Fechar modal de informações de leitura ao clicar fora
      if (
        showReadInfo &&
        !(event.target as Element).closest(".read-info-container")
      ) {
        setShowReadInfo(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mentionProfile, showReadInfo]);

  // Resetar o controle de carregamento inicial ao trocar de sala
  useEffect(() => {
    hasLoadedInitially.current = false;
    setMessages([]);
    setCurrentPage(1);
    setHasMoreMessages(true);
    setHasInitialScrolled(false);
    setActiveMention(null);
    setMentionProfile(null);
  }, [room?.id]);

  useEffect(() => {
    loadMessages(1, true);
  }, [room?.id, loadMessages]);

  // Foco automático no input quando o chat abre/não está minimizado
  useEffect(() => {
    if (!isMinimized && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isMinimized, room?.id]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container && hasInitialScrolled) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll, hasInitialScrolled]);

  useEffect(() => {
    if (!room?.id) return;

    const eventName = `notification:chat:novamensagem:${room.id}`;
    const deleteEventName = `notification:chat:mensagemdeletada:${room.id}`;

    const messageHandler = (data: any) => {
      const messageData = normalizeRealtimeMessage(data);
      setMessages((prev) => upsertRealtimeMessage(prev, messageData));
    };

    // Handler para mensagem deletada
    const deleteHandler = (data: MessageDeletedPayload) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === data.messageId
            ? { ...msg, deleted: true, deleted_at: data.deletedAt }
            : msg,
        ),
      );
    };

    const setupListener = () => {
      socket.off(eventName, messageHandler);
      socket.off(deleteEventName, deleteHandler);
      socket.on(eventName, messageHandler);
      socket.on(deleteEventName, deleteHandler);
    };

    // Se o socket já está conectado, configurar imediatamente
    if (socket.connected) {
      setupListener();
    } else {
      const onConnect = () => {
        setupListener();
      };
      socket.on("connect", onConnect);

      // Tentar conectar
      socket.connect();

      return () => {
        socket.off("connect", onConnect);
        socket.off(eventName, messageHandler);
        socket.off(deleteEventName, deleteHandler);
      };
    }

    return () => {
      socket.off(eventName, messageHandler);
      socket.off(deleteEventName, deleteHandler);
    };
  }, [room?.id, userLogged.data?.id]);

  useEffect(() => {
    // Scroll imediato para baixo apenas em casos específicos
    if (!isLoadingRef.current && !hasInitialScrolled) {
      // Scroll inicial - ir direto para o final sem animação
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
        setHasInitialScrolled(true);
      }
    } else if (!isLoadingRef.current && hasInitialScrolled) {
      // Scroll suave apenas para novas mensagens (não ao carregar antigas)
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isMinimized, hasInitialScrolled]);

  // useEffect para controlar o scroll ao mudar de viewMode
  useEffect(() => {
    if (viewMode === "readInfo") {
      // Quando muda para tela de informações, scroll para o topo
      setTimeout(() => {
        const readInfoContainer = readInfoContainerRef.current;
        if (readInfoContainer) {
          readInfoContainer.scrollTop = 0;
        }
      }, 50);
    }
  }, [viewMode]);

  const handleExpandChat = () => {
    navigate(`/chat-expandido/${room?.id}`);

    setTimeout(() => {
      onClose({ preserveCall: true });
      setMenuOpen?.(false);
      setSelectedOption?.(null);
    }, 50);
  };

  return (
    <div
      className={`w-full shadow-xl rounded-t-lg bg-white h-full flex justify-center flex-col overflow-hidden`}
    >
      {/* Header */}
      <div
        className={`grid grid-cols-10 items-center justify-between p-3 text-balck rounded-t-lg ${!!isMinimized ? "bg-white" : "bg-[#E4EAFF]"}`}
      >
        <NavLink to="/mensagens" className="sm:hidden col-span-1">
          <IconArrowDown className="rotate-90" size={20} />
        </NavLink>

        <div className="flex items-center gap-2 col-span-5 sm:col-span-6">
          <div className="relative">
            <img
              src={getChatImage()}
              alt={getChatTitle()}
              className="w-8 h-8 rounded-full object-cover"
            />
            {isGroupChat && (
              <div className="absolute -bottom-1 -right-1 bg-white text-azul-400 rounded-full p-0.5">
                <Users size={8} />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate text-black">
              {viewMode === "readInfo"
                ? t("chatModule.read_info.title")
                : getChatTitle()}
            </p>
            <p
              className="text-xs opacity-90 truncate max-w-[90%] cursor-help text-black"
              title={getGroupMembers().fullList}
            >
              {viewMode === "readInfo" ? (
                <p className="text-xs">
                  {t("chatModule.read_info.sent_at")}{" "}
                  {new Date(selectedMessage.created_at).toLocaleString("pt-BR")}
                </p>
              ) : (
                <>{getGroupMembers().names}</>
              )}
            </p>
          </div>
        </div>

        <div className="flex gap-1 ml-auto col-span-4 justify-end">
          <button
            onClick={handleExpandChat}
            className="p-1 text-black hover:bg-azul-300 hover:text-white duration-300 rounded transition-colors hidden sm:flex flex-col items-center justify-center"
          >
            <SquareArrowOutUpRight size={16} />
          </button>

          {/* Botão de voltar - só aparece na tela de informações */}
          {viewMode === "readInfo" && (
            <button
              onClick={handleBackToChat}
              className="p-1 text-black hover:bg-azul-300 hover:text-white duration-300 rounded transition-colors"
              title={t("chatModule.read_info.back_to_chat")}
            >
              <IconArrowDown className="rotate-90" size={16} />
            </button>
          )}

          {/* Botões normais - só aparecem na tela de chat */}
          {viewMode === "chat" && (
            <>
              {!isGroupChat && (
                <button
                  onClick={() => void startChatVoiceCall(room)}
                  disabled={isChatCallBusy}
                  className={`p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    activeChatCall?.roomId === String(room?.id) &&
                    ["calling", "answered", "in_progress"].includes(
                      activeChatCall?.rawStatus || activeChatCall?.status || "",
                    )
                      ? "text-azul-400 bg-azul-50"
                      : "text-black hover:bg-azul-300 hover:text-white"
                  }`}
                  title="Iniciar chamada de voz"
                >
                  <Phone size={16} />
                </button>
              )}

              {/* Botão de arquivos e links */}
              <button
                onClick={() => setShowFilesModal(true)}
                className="p-1 text-black hover:bg-azul-300 hover:text-white rounded transition-colors"
                title={t("chatFeatures.files.title")}
              >
                <Folder size={16} />
              </button>

              {!!deskId && (
                <button
                  onClick={handleOpenDeskComments}
                  disabled={openingDeskComments}
                  className="p-1 text-black hover:bg-azul-300 hover:text-white rounded transition-colors disabled:cursor-wait disabled:opacity-60"
                  title="Abrir comentários da Desk"
                >
                  <MessageCircle size={16} />
                </button>
              )}

              {/* Botão de editar grupo - só aparece se for grupo e usuário for admin */}
              {isGroupChat && isGroupAdmin && (
                <button
                  onClick={() => setShowEditGroupModal(true)}
                  className="p-1 text-black hover:bg-azul-300 hover:text-white rounded transition-colors"
                  title={t("chatModule.modal.edit_group")}
                >
                  <Settings size={16} />
                </button>
              )}
              <button
                onClick={onMinimize}
                className={`transition-all hidden sm:block p-1 hover:bg-azul-300 hover:text-white duration-300 rounded text-black ${!isMinimized ? "rotate-180" : ""}`}
              >
                <ChevronUp />
              </button>
            </>
          )}

          <button
            onClick={() => onClose()}
            className="p-1 hover:bg-azul-300 hover:text-white duration-300 rounded text-black"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Conteúdo baseado no modo de visualização */}
          {viewMode === "chat" ? (
            <>
              {/* Mensagens */}
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto overflow-x-hidden bg-white p-3 space-y-3"
              >
                {isLoadingMore && (
                  <div className="text-center py-2">
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-azul-400"></div>
                  </div>
                )}

                {messages.map((message, index) => {
                  const isOwn = message.sender_id === userLogged?.data?.id;
                  const sender = room?.users?.find(
                    (u: any) => u.id === message.sender_id,
                  );
                  const showDate =
                    index === 0 ||
                    formatDate(messages[index - 1]?.created_at) !==
                      formatDate(message.created_at);
                  const readStatus = getMessageReadStatus(message);
                  const mentionsCurrentUser =
                    !isOwn && messageMentionsCurrentUser(message.content);
                  const isCallEvent = message.event_type === "call";

                  // Se a mensagem foi deletada, mostrar placeholder
                  if (message.deleted) {
                    return (
                      <div key={message.id || index}>
                        {showDate && (
                          <div className="text-center py-2">
                            <span className="bg-gray-200 px-3 py-1 rounded-full text-xs text-gray-600">
                              {formatDate(message.created_at)}
                            </span>
                          </div>
                        )}
                        <DeletedMessagePlaceholder
                          timestamp={message.created_at}
                          isOwnMessage={isOwn}
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={message.id || index} data-message-id={message.id}>
                      {showDate && (
                        <div className="text-center py-2">
                          <span className="bg-gray-200 px-3 py-1 rounded-full text-xs text-gray-600">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                      )}

                      <div
                        className={`flex ${
                          isOwn ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md xl:max-w-lg min-w-0 ${
                            isOwn ? "order-2" : "order-1"
                          } relative transition-all duration-300`}
                          onMouseEnter={() => setHoveredMessage(message.id)}
                          onMouseLeave={() => {
                            setHoveredMessage(null);
                            setShowReadInfo(null);
                          }}
                        >
                          {!isOwn && isGroupChat && (
                            <p className="text-xs text-gray-500 mb-1 px-1">
                              {sender?.name} {sender?.last_name} -{" "}
                              {formatTime(message.created_at)}
                            </p>
                          )}
                          <div
                            className={`px-3 py-2 rounded-[23px] break-words overflow-wrap-anywhere ${
                              isOwn
                                ? "bg-[#153ECE] text-white"
                                : mentionsCurrentUser
                                  ? "bg-[#E4EAFF]/60 ring-1 ring-[#153DCE]/15 text-gray-900"
                                  : "bg-[#F3F3F3]"
                            }`}
                            style={{ overflowWrap: "anywhere" }}
                          >
                            {/* Reply Quote - se esta mensagem é uma resposta */}
                            {message.reply_to && (
                              <ReplyQuote
                                replyTo={message.reply_to}
                                isOwnMessage={isOwn}
                                onClick={() =>
                                  handleScrollToMessage(message.reply_to.id)
                                }
                              />
                            )}

                            <div className="text-sm break-words">
                              {isCallEvent ? (
                                <CallHistoryMessage
                                  message={message}
                                  currentUserId={userLogged?.data?.id}
                                  isOwnMessage={isOwn}
                                />
                              ) : (
                                renderMessageContent(message, isOwn)
                              )}
                            </div>
                            <div className="flex items-end justify-end gap-1 mt-1">
                              <p
                                className={`text-[10px] leading-none ${
                                  isOwn ? "text-white/70" : "text-gray-500"
                                }`}
                              >
                                {formatTime(message.created_at)}
                              </p>
                              {isOwn && (
                                <div className="flex items-center gap-1">
                                  {readStatus.status === "read" ? (
                                    <CheckCheck
                                      size={12}
                                      className="text-white/70"
                                    />
                                  ) : (
                                    <Check
                                      size={12}
                                      className="text-white/70"
                                    />
                                  )}
                                  {isGroupChat && readStatus.count > 0 && (
                                    <span className="text-[10px] text-white/70">
                                      {readStatus.count}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Menu de ações (Reply, Delete, etc) */}
                          <MessageActions
                            message={message as ChatMessage}
                            isVisible={hoveredMessage === message.id}
                            isOwnMessage={isOwn}
                            canReply={!message.reply_to_id && !isCallEvent}
                            onReply={handleReply}
                            onDelete={handleDeleteClick}
                            onCopy={handleCopyMessage}
                            onInfo={
                              isOwn && message.readBy
                                ? handleOpenReadInfo
                                : undefined
                            }
                            showInfo={isOwn && !!message.readBy}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Preview de Reply */}
              <AnimatePresence>
                {replyToMessage && (
                  <ReplyPreview
                    replyTo={replyToMessage}
                    onCancel={handleCancelReply}
                  />
                )}
              </AnimatePresence>

              {selectedFiles.length > 0 && (
                <div className="flex-shrink-0 px-3 py-2 bg-gray-100 border-t">
                  <div className="flex gap-2 flex-wrap">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-white px-2 py-1 rounded text-xs"
                      >
                        <span className="truncate max-w-32">{file.name}</span>
                        <button
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Input de mensagem */}
              <div className="p-4 relative">
                {/* Picker de Emoji Rápido */}
                {showEmojiPicker && (
                  <div
                    ref={emojiPickerRef}
                    className="absolute bottom-full left-4 right-4 mb-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50"
                  >
                    <div className="grid grid-cols-6 gap-2">
                      {quickEmojis.map((emoji, index) => (
                        <button
                          key={index}
                          onClick={() => addEmoji(emoji)}
                          className="w-8 h-8 text-lg hover:bg-gray-100 rounded transition-colors flex items-center justify-center"
                          title={t("chatModule.emoji.add", { emoji })}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {activeMention && filteredMentionUsers.length > 0 && (
                  <div
                    ref={mentionListRef}
                    className="absolute bottom-full left-4 right-4 mb-2 z-50 overflow-hidden rounded-lg border border-cinza-200 bg-white shadow-lg"
                  >
                    {filteredMentionUsers.map((user, index) => {
                      const userName = getMentionUserName(user);
                      const userAvatar = getMentionUserAvatar(user);
                      const isSelected = index === selectedMentionIndex;

                      return (
                        <button
                          key={user.id}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            insertMention(user);
                          }}
                          className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                            isSelected
                              ? "bg-[#E4EAFF] text-azul-400"
                              : "hover:bg-cinza-100"
                          }`}
                        >
                          {userAvatar ? (
                            <img
                              src={userAvatar}
                              alt={userName}
                              className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
                            />
                          ) : (
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-azul-400 text-xs font-semibold text-white">
                              {userName[0]?.toUpperCase()}
                            </span>
                          )}
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium">
                              {userName}
                            </span>
                            {user.email && (
                              <span className="block truncate text-xs text-cinza-500">
                                {user.email}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center gap-1 bg-[#F3F3F3] border-2 border-[#F3F3F3] rounded-2xl sm:rounded-full pr-2 min-h-[44px]">
                  <div className="flex items-center flex-1">
                    <textarea
                      ref={textareaRef}
                      value={newMessage}
                      onChange={handleTextareaChange}
                      onSelect={(event) =>
                        setActiveMention(
                          getActiveMention(
                            event.currentTarget.value,
                            event.currentTarget.selectionStart,
                          ),
                        )
                      }
                      placeholder={
                        replyToMessage
                          ? t("chatFeatures.reply.replyingTo") +
                            " " +
                            (replyToMessage.sender?.name || "...")
                          : t("chatModule.input.placeholder")
                      }
                      className="w-full px-2 sm:px-6 py-4 rounded-2xl sm:rounded-full resize-none focus:outline-none overflow-y-auto bg-transparent bg-white"
                      rows={1}
                      style={{
                        minHeight: "28px",
                        maxHeight: "120px",
                        lineHeight: "1.5",
                      }}
                      onKeyDown={(e) => {
                        if (activeMention && filteredMentionUsers.length > 0) {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setSelectedMentionIndex((prev) =>
                              Math.min(
                                prev + 1,
                                filteredMentionUsers.length - 1,
                              ),
                            );
                            return;
                          }
                          if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setSelectedMentionIndex((prev) =>
                              Math.max(prev - 1, 0),
                            );
                            return;
                          }
                          if (e.key === "Enter" || e.key === "Tab") {
                            e.preventDefault();
                            insertMention(
                              filteredMentionUsers[selectedMentionIndex],
                            );
                            return;
                          }
                          if (e.key === "Escape") {
                            e.preventDefault();
                            setActiveMention(null);
                            return;
                          }
                        }

                        // Ctrl+Enter para enviar
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          handleSendMessage();
                          return;
                        }
                        // Enter para enviar (sem Shift)
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                          return;
                        }
                        // Escape para cancelar reply
                        if (e.key === "Escape" && replyToMessage) {
                          e.preventDefault();
                          handleCancelReply();
                          return;
                        }
                      }}
                    />
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className={`p-1 rounded transition-colors w-7 ${
                      showEmojiPicker
                        ? "text-azul-400 bg-azul-50"
                        : "text-gray-500 hover:text-azul-400"
                    }`}
                    title={t("chatModule.emoji.add_emoji")}
                  >
                    <IconSmileChat />
                  </button>

                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1 text-gray-500 hover:text-azul-400 rounded w-6 h-7"
                  >
                    <IconUploadChat />
                  </button>

                  <button
                    onClick={() => setShowAudioRecorder(!showAudioRecorder)}
                    className={`p-1 rounded transition-colors w-6 h-7 ${
                      showAudioRecorder
                        ? "text-azul-400 bg-azul-50"
                        : "text-gray-500 hover:text-azul-400 hover:bg-gray-100"
                    }`}
                    title={t("chatModule.audio.record")}
                  >
                    <IconAudioChat />
                  </button>

                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() && selectedFiles.length === 0}
                    className="w-8 h-8 p-2 bg-[#153ECE] text-white rounded-full hover:bg-azul-500 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                  >
                    <IconSendChat />
                  </button>
                </div>
              </div>

              {/* AudioRecorder */}
              {showAudioRecorder && (
                <div className="p-3 border-t bg-white">
                  <AudioRecorder
                    onAudioReady={handleAudioReady}
                    isSending={isSending}
                    onCancel={handleCancelAudio}
                  />
                </div>
              )}
            </>
          ) : (
            /* Página de informações de leitura */
            renderReadInfoPage()
          )}
        </>
      )}

      {mentionProfile && (
        <div
          ref={mentionProfileRef}
          style={{
            position: "fixed",
            top: mentionProfile.top,
            left: mentionProfile.left,
          }}
          className="z-[80] w-64 rounded-lg border border-cinza-200 bg-white p-4 text-black shadow-xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {getMentionUserAvatar(mentionProfile.user) ? (
                <img
                  src={getMentionUserAvatar(mentionProfile.user)}
                  alt={getMentionUserName(mentionProfile.user)}
                  className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-azul-400 text-sm font-semibold text-white">
                  {getMentionUserName(mentionProfile.user)[0]?.toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">
                  {getMentionUserName(mentionProfile.user)}
                </p>
                {mentionProfile.user.email && (
                  <p className="truncate text-xs text-cinza-500">
                    {mentionProfile.user.email}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMentionProfile(null)}
              className="rounded p-1 text-cinza-500 transition-colors hover:bg-cinza-100 hover:text-black"
              title="Fechar"
            >
              <X size={14} />
            </button>
          </div>
          <div className="mt-3 rounded-md bg-cinza-100 px-3 py-2 text-xs text-cinza-500">
            Participante deste chat
          </div>
        </div>
      )}

      {/* Modal de Edição do Grupo */}
      <EditGroupModal
        isOpen={showEditGroupModal}
        onClose={() => setShowEditGroupModal(false)}
        room={room}
        onUpdate={handleGroupUpdate}
      />

      {/* Modal de Confirmação de Delete */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMessageToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      {/* Modal de Upload de Arquivos */}
      <FileUploadModal
        isOpen={showUploadModal}
        initialFiles={pendingFiles}
        onClose={() => {
          setShowUploadModal(false);
          setPendingFiles([]);
        }}
        onSend={handleUploadModalSend}
      />

      {/* Modal de Arquivos e Links */}
      <FilesAndLinksModal
        isOpen={showFilesModal}
        roomId={room?.id}
        currentUserId={userLogged?.data?.id}
        isAdmin={isGroupAdmin}
        onClose={() => setShowFilesModal(false)}
      />

      {!!deskCommentsData && (
        <ResumoDesk
          togleOpen={setDeskCommentsData}
          data={deskCommentsData}
          client={deskCommentsData?.cliente?.name_fantasy}
          agency={deskCommentsData?.agencia?.name_fantasy}
          status={deskCommentsData?.status?.name}
          type="desk"
          initialTab="chat"
        />
      )}
    </div>
  );
}
